/**
 * One-shot claim for reporting `auth_load_failed`, scoped to the DOCUMENT.
 *
 * Why this exists (measured on production PostHog, project 375980, 2026-08-30):
 * every clerk-js load failure was arriving as a same-second PAIR of events with
 * different `waited_ms`, and the pair at 2026-08-29 15:31:50 disagreed on the
 * cause -- one said `reason: 'timeout'`, its twin said `reason: 'clerk_error'`.
 * Two independently computed values means two live component instances, each
 * passing its own per-instance `useRef` guard. Every raw count of the outage
 * was therefore roughly double, which is how an external summary of it reported
 * "10 affected users" against a true distinct-person count of 7.
 *
 * The guard was per-instance; the condition it reports ("clerk-js did not load
 * in this document") is per-document. This makes the two agree.
 *
 * Deliberately a plain module-level flag rather than sessionStorage, unlike its
 * sibling `clerkLoadRetry`. The two claims have different lifetimes on purpose:
 *
 *   - the RETRY claim must survive the reload it triggers, or "exactly once"
 *     is unenforceable and a Clerk outage becomes an infinite reload loop, so
 *     it needs storage that outlives the document;
 *   - this REPORT claim must NOT survive it. The reloaded document is a second
 *     attempt and should say so (`attempt: 2`). A fresh JS context starts with
 *     `reported = false`, so that falls out for free.
 *
 * It also cannot throw. `clerkLoadRetry` has to guard a `window.sessionStorage`
 * dereference that raises SecurityError on an opaque origin; a module-level
 * boolean has no such failure mode, which is a second reason not to reach for
 * storage here.
 */

let reported = false;

/**
 * Claim the single `auth_load_failed` report for this document.
 *
 * @returns true only for the first caller in this JS context. Every later
 *   caller gets false and must stay silent -- it is a duplicate view of one
 *   page-level outage, not a second outage.
 */
export function claimAuthLoadFailureReport(): boolean {
  if (reported) return false;
  reported = true;
  return true;
}

/**
 * Reset the claim. TEST-ONLY.
 *
 * A real document gets a fresh module instance on every load, so production
 * never needs this; jest keeps one module registry across the tests in a file.
 * This is the direct analogue of `window.sessionStorage.clear()` in the
 * existing auto-retry suites -- a visible reset rather than a hidden one.
 */
export function resetAuthLoadFailureReport(): void {
  reported = false;
}
